import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import searchIcon from 'assets/images/icons/search.svg';
import TextField from 'components/form-components/TextField';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import DateRangeDropdown from 'pages/tickets/tickets/DateRangeDropdown';
import ScheduleTasksActions from './ScheduleTasksActions';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import sortIcon from 'assets/images/icons/sort-arrows-grey.svg';
import {
  taskDueDateFilterOptions,
  taskStatusFilterOptions,
  schedulesSortOptions,
} from '../../utils';
import {
  generateTextWithSuffix,
  getDisplayValueFromList,
  priorityOptionsList,
} from 'components/utils';

const ScheduleTasksHeader = ({
  filter,
  setFilter,
  handleDebouncedFilter,
  taskLabelsOptions,
  companyFieldOptions,
  selectedRows,
  actionsCallBack,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');

  const handleFilterChange = (key, value) => {
    setFilter((prevState) => ({ ...prevState, [key]: value }));
  };

  return (
    <div className='mb-3 d-flex gap-2 flex-wrap justify-content-between align-items-end'>
      <div className='d-flex gap-3'>
        <TextField
          value={searchText}
          name='search'
          handleChange={(_, value) => setSearchText(value)}
          placeholder={'field_search'}
          icon={searchIcon}
          debounceFunc={handleDebouncedFilter}
          shrink
        />

        <ScheduleTasksActions
          ids={selectedRows}
          isActionButton={false}
          actionsCallBack={actionsCallBack}
        />
      </div>
      <div className='d-flex flex-wrap gap-3 user-select-none align-items-end'>
        <MultiLevelSelect
          options={{ children: schedulesSortOptions }}
          value={[]}
          name={'sortBy'}
          handleChange={(field, value) => handleFilterChange(field, value[0])}
          align='end'
          toggleClasses='p-2 d-flex justify-content-between align-items-center cursor-pointer'
          toggleComponent={
            <>
              <img className='mx-2' src={sortIcon} />
              <label className='cursor-pointer'>
                {t(getDisplayValueFromList(schedulesSortOptions, filter.sortBy, 'field_sort_by'))}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
        />

        <MultiLevelSelect
          name='priority'
          displayName='field_priority'
          options={{ children: priorityOptionsList, selectMultiple: true }}
          value={filter.priority || []}
          handleChange={handleFilterChange}
          toggleClasses='p-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='cursor-pointer overflow-hidden'>
                {generateTextWithSuffix(
                  (filter.priority || [])
                    .map((v) => {
                      const key = priorityOptionsList.find((o) => o.value === v)?.displayValue;
                      return key ? t(key) : null;
                    })
                    .filter(Boolean),
                  2,
                  (count) => ` +${count} more`,
                  'select_select',
                  t,
                )}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          iconVariant={'original'}
          fullWidth
        />

        <MultiLevelSelect
          name='labels'
          displayName='field_label'
          options={{ children: taskLabelsOptions, selectMultiple: true }}
          value={filter.labels || []}
          handleChange={handleFilterChange}
          toggleClasses='p-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='cursor-pointer overflow-hidden'>
                {generateTextWithSuffix(
                  (filter.labels || [])
                    .map((v) => taskLabelsOptions.find((o) => o.value === v)?.displayValue)
                    .filter(Boolean),
                  2,
                  (count) => ` +${count} more`,
                  'select_select',
                  t,
                )}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          fullWidth
        />

        <MultiLevelSelect
          name='companyFieldValues'
          displayName='field_company_field'
          options={{ children: companyFieldOptions }}
          value={filter.companyFieldValues || []}
          handleChange={handleFilterChange}
          toggleClasses='p-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='cursor-pointer overflow-hidden'>
                {filter.companyFieldValues?.length > 0
                  ? `${filter.companyFieldValues.length} value(s) selected`
                  : t('select_select')}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          fullWidth
        />

        <MultiLevelSelect
          name='status'
          displayName='field_status'
          options={{ children: taskStatusFilterOptions, selectMultiple: true }}
          value={filter.status || []}
          handleChange={handleFilterChange}
          toggleClasses='p-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='cursor-pointer overflow-hidden'>
                {generateTextWithSuffix(
                  (filter.status || [])
                    .map((v) => {
                      const key = taskStatusFilterOptions.find((o) => o.value === v)?.displayValue;
                      return key ? t(key) : null;
                    })
                    .filter(Boolean),
                  2,
                  (count) => ` +${count} more`,
                  'select_select',
                  t,
                )}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          fullWidth
        />

        <div className='d-flex flex-column gap-1'>
          <span>{t('field_due_date')}</span>
          <DateRangeDropdown
            name='dueDate'
            value={filter.dueDate}
            optionsList={taskDueDateFilterOptions}
            handleChange={handleFilterChange}
            toggleClasses='p-2 border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='cursor-pointer'>
                  {t(
                    getDisplayValueFromList(
                      taskDueDateFilterOptions,
                      filter.dueDate?.type,
                      'select_select',
                    ),
                  )}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
          />
        </div>
      </div>
    </div>
  );
};

ScheduleTasksHeader.propTypes = {
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  handleDebouncedFilter: PropTypes.func.isRequired,
  taskLabelsOptions: PropTypes.array.isRequired,
  companyFieldOptions: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  actionsCallBack: PropTypes.func,
};

export default ScheduleTasksHeader;
